import React, { useLayoutEffect, useRef } from 'react';
import TransientThoughtFeedItem from './TransientThoughtFeedItem';

// Paused-interaction view. Visual parity with the live history feed: same
// bubble/cloud rendering, same per-index slot logic. No live animation —
// these are static, scrolled like the prev-comments scroll on the live screen.
// `data` is oldest-first (matches the Talking Tiles result board): the view
// opens scrolled to the bottom (newest), and scrolling up pages in older
// comments above the fold.
const TransientThoughtResult = ({
  data = [],
  settings = {},
  colors = {},
  fontFamily,
  fontSize,
  isBubbleStyle,
  ttId,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  sentinelRef = undefined
}) => {
  const scrollRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const hasScrolledToBottomRef = useRef(false);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (!hasScrolledToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      hasScrolledToBottomRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    // Older comments render above the fold when a page is paged in — hold the
    // viewport on what the user was looking at instead of jumping to the top.
    const newScrollHeight = el.scrollHeight;
    const delta = newScrollHeight - prevScrollHeightRef.current;
    if (delta > 0) el.scrollTop += delta;
    prevScrollHeightRef.current = newScrollHeight;
  }, [data]);

  if (!data.length && !isLoading) {
    return (
      <div className="overflow-y-auto flex flex-col p-3 gap-y-2 z-10 h-full">
        <p className="text-base text-center font-OpenSans font-semibold text-neutral-60 lg:text-xl italic md:leading-6 lg:leading-7 overflow-hidden break-words">
          No comments to display
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="overflow-y-auto w-full h-full">
      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-2 shrink-0">
          {isLoadingMore && <span className="text-sm text-neutral-60 font-OpenSans">Loading older comments...</span>}
        </div>
      )}
      <div className="w-full flex flex-col gap-3 px-4 py-6 md:px-8">
        {data.map((item, idx) => (
          <TransientThoughtFeedItem
            key={item.id || `${ttId}-result-${idx}`}
            words={item.body || item.words}
            username={item.username}
            index={idx}
            isBubbleStyle={isBubbleStyle}
            colors={colors}
            fontFamily={fontFamily}
            fontSize={fontSize}
            transientThoughtShowUsername={settings?.transientThoughtShowUsername}
            positionKey={idx % 2 === 0 ? 'topLeft' : 'topRight'}
          />
        ))}
      </div>
    </div>
  );
};

export default TransientThoughtResult;
